import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CACHE_KEY_CONTRACTORS } from "../../../utils/constants";
import contractorService from "../../../services/ticketService/contractorService";
import { ContractorInterface } from "../../../models/TicketModule/Contractor";

interface UpdateContractorsContext {
  previousContractors: ContractorInterface[];
}

const useUpdateContractors = (page: number, pageSize: number) => {
  const queryClient = useQueryClient();

  return useMutation<
    ContractorInterface,
    Error,
    ContractorInterface,
    UpdateContractorsContext
  >({
    mutationFn: async (contractors: ContractorInterface) => {
      // Assuming the ID is stored in the contractor object or can be derived
      const { id, ...contractorData } = contractors;
      return contractorService.Update(id || "", contractorData);
    },

    onMutate: async (updatedContractors: ContractorInterface) => {
      await queryClient.cancelQueries({
        queryKey: [
          CACHE_KEY_CONTRACTORS,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
      });

      const previousContractors =
        queryClient.getQueryData<ContractorInterface[]>([
          CACHE_KEY_CONTRACTORS,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ]) || [];

      queryClient.setQueryData<ContractorInterface[]>(
        [
          CACHE_KEY_CONTRACTORS,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        (contractors = []) =>
          contractors.map((contractor) =>
            contractor.id === updatedContractors.id
              ? updatedContractors
              : contractor
          )
      );

      return { previousContractors };
    },

    onSuccess: (savedContractor) => {
      queryClient.setQueryData<ContractorInterface[]>(
        [
          CACHE_KEY_CONTRACTORS,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        (contractors) =>
          contractors?.map((contractor) =>
            contractor.id === savedContractor.id ? savedContractor : contractor
          )
      );
    },

    onError: (error, updatedContractors, context) => {
      if (!context) return;

      queryClient.setQueryData<ContractorInterface[]>(
        [
          CACHE_KEY_CONTRACTORS,
          page > 0 ? page : null,
          pageSize > 0 ? pageSize : null,
        ],
        context.previousContractors
      );
    },
  });
};

export default useUpdateContractors;
